import { useId, useRef, useState } from 'react';
import { Box, FormHelperText, List, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import { ActionIconButton } from 'framework/ui/ui-builder';
import AttachFile from '@mui/icons-material/AttachFile';
import Close from '@mui/icons-material/Close';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { compact, uniq } from 'lodash';
import { getHumanReadableDataSize } from 'framework/utils';
import type { FillInputProps } from '../fillInput';
import { numberOf } from '../shared/bounds';
import { acceptOf, limitsText, processFiles, type AttachedFile, type AttachmentSettings } from 'framework/files';

export const AttachmentInput = (props: FillInputProps<AttachedFile[], AttachmentSettings>) => {
  const { settings, inputId, describedBy, value: files, onChange, disabled, error, bounds, attachFile } = props;
  const id = useId();
  const limitsId = `${id}limits`;
  const refusedId = `${id}refused`;
  const chooser = useRef<HTMLInputElement>(null);
  const [refused, setRefused] = useState<string[]>([]);

  const max = numberOf(bounds.max);
  const room = max === undefined ? Infinity : max - files.length;
  const limits = limitsText(settings, bounds);

  const attach = (chosen: FileList | null) => {
    if (chooser.current) chooser.current.value = '';
    if (!chosen?.length) return;
    const { added, notAttached } = processFiles({ chosen, settings, max, room, attachFile });
    setRefused(notAttached);
    if (added.length > 0) onChange([...files, ...added]);
  };

  return (
    <>
      {files.length > 0 && (
        <List dense disablePadding>
          {files.map((file, index) => (
            <ListItem
              key={file.fileId}
              disableGutters
              secondaryAction={
                <ActionIconButton
                  label={`Remove ${file.name}`}
                  tooltip={disabled ? undefined : 'Remove'}
                  icon={<Close />}
                  variant="text"
                  color="default"
                  size="medium"
                  disabled={disabled}
                  onClick={() => onChange(files.filter((_, at) => at !== index))}
                />
              }
            >
              <ListItemIcon>
                <InsertDriveFileOutlined />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={getHumanReadableDataSize(file.size)}
                slotProps={{ primary: { noWrap: true, title: file.name } }}
              />
            </ListItem>
          ))}
        </List>
      )}
      <Stack useFlexGap direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {room > 0 && (
          <Button
            id={inputId}
            variant="outlined"
            color={error ? 'error' : 'primary'}
            startIcon={<AttachFile />}
            disabled={disabled}
            aria-describedby={describedByIds(describedBy, limits && limitsId, refused.length > 0 && refusedId)}
            onClick={() => chooser.current?.click()}
          >
            Attach file
          </Button>
        )}
        {limits && (
          <FormHelperText id={limitsId} error={false}>
            {limits}
          </FormHelperText>
        )}
      </Stack>
      <input
        ref={chooser}
        type="file"
        hidden
        accept={acceptOf(settings)}
        multiple={room > 1}
        onChange={event => attach(event.target.files)}
      />
      <Box id={refusedId}>
        {uniq(refused).map(line => (
          <FormHelperText key={line} error>
            {line}
          </FormHelperText>
        ))}
      </Box>
    </>
  );
};

const describedByIds = (...ids: (string | false | undefined)[]): string | undefined => {
  return compact(ids).join(' ') || undefined;
};
